// PRC-031: esqueletos en lugar de ruedas. Cada bloque muestra su forma en gris
// con su altura final, para que la página no salte al cargar.
import React from 'react';
import { Box } from '@mui/material';
import PageHeader from './PageHeader';

const shimmer = {
  bgcolor: 'rgba(148,163,184,.09)',
  backgroundImage: 'linear-gradient(90deg, rgba(148,163,184,0) 0%, rgba(148,163,184,.08) 50%, rgba(148,163,184,0) 100%)',
  backgroundSize: '200% 100%',
  animation: 'proaShimmer 1.4s ease-in-out infinite',
  '@keyframes proaShimmer': { from: { backgroundPosition: '200% 0' }, to: { backgroundPosition: '-200% 0' } },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

export const SkeletonBlock: React.FC<{ h: number | string; w?: number | string; radius?: number | string; sx?: object }> = ({ h, w = '100%', radius = '12px', sx }) => (
  <Box aria-hidden sx={{ width: w, height: h, borderRadius: radius, ...shimmer, ...sx }} />
);

// Fila de tarjetas de indicador (misma altura que MetricCard / tarjetas de Overview).
export const SkeletonMetrics: React.FC<{ count?: number; h?: number }> = ({ count = 4, h = 96 }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: `repeat(${count}, minmax(0,1fr))` }, gap: 2 }}>
    {Array.from({ length: count }).map((_, i) => <SkeletonBlock key={i} h={h} />)}
  </Box>
);

// Tabla: cabecera y filas con la altura de una fila real.
export const SkeletonTable: React.FC<{ rows?: number; rowHeight?: number }> = ({ rows = 8, rowHeight = 44 }) => (
  <Box sx={{ border: '1px solid rgba(148,163,184,.12)', borderRadius: '12px', overflow: 'hidden' }}>
    <SkeletonBlock h={40} radius={0} sx={{ bgcolor: 'rgba(148,163,184,.12)' }} />
    {Array.from({ length: rows }).map((_, i) => (
      <Box key={i} sx={{ height: rowHeight, display: 'flex', alignItems: 'center', gap: 2, px: 2, borderTop: '1px solid rgba(148,163,184,.08)' }}>
        <SkeletonBlock h={12} w="28%" radius="6px" />
        <SkeletonBlock h={12} w="18%" radius="6px" />
        <Box sx={{ flex: 1 }} />
        <SkeletonBlock h={12} w="12%" radius="6px" />
      </Box>
    ))}
  </Box>
);

export type PageSkeletonVariant = 'dashboard' | 'table' | 'detail' | 'cards';

// Página completa: la cabecera real (estructura al instante) y los bloques en gris.
export const PageSkeleton: React.FC<{
  eyebrow?: string;
  title?: string;
  variant?: PageSkeletonVariant;
  // Sin cabecera propia (la pantalla ya pinta la suya).
  bare?: boolean;
  padded?: boolean;
}> = ({ eyebrow, title, variant = 'dashboard', bare, padded = true }) => (
  <Box aria-busy="true" aria-label="Cargando" sx={{ width: '100%', ...(padded ? { px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 6 } : {}) }}>
    {!bare && eyebrow && title && <PageHeader eyebrow={eyebrow} title={title} />}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {variant === 'dashboard' && (
        <>
          <SkeletonBlock h={56} />
          <SkeletonMetrics count={4} h={112} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.6fr) minmax(320px,.85fr)' }, gap: 2 }}>
            <SkeletonBlock h={340} radius="16px" />
            <SkeletonBlock h={340} radius="16px" />
          </Box>
          <SkeletonBlock h={280} radius="16px" />
        </>
      )}
      {variant === 'table' && (
        <>
          <SkeletonBlock h={56} />
          <SkeletonTable rows={10} />
        </>
      )}
      {variant === 'detail' && (
        <>
          <SkeletonBlock h={48} />
          <SkeletonMetrics count={4} h={104} />
          <SkeletonBlock h={320} radius="16px" />
          <SkeletonBlock h={240} radius="16px" />
        </>
      )}
      {variant === 'cards' && (
        <>
          <SkeletonMetrics count={3} h={96} />
          <SkeletonTable rows={5} rowHeight={56} />
        </>
      )}
    </Box>
  </Box>
);

export default PageSkeleton;
